import React, { FC, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { focusIds, orientation } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { ScrollContainer } from '@/containers/styles'
import { useMenuItem } from '@/hooks/useMenuItem'
import { SubMenuPropsType } from '@/types/componentsPropsTypes'
import { MenuItemType } from '@/types/menuTypes'

import { useSubMenuFocus } from './hooks'
import { MenuItem } from './MenuItem'
import { SubMainMenu, SubMenuPlaceholder } from './styles'

export const SubMenu: FC<SubMenuPropsType> = ({
  onMenuFocused,
  onMenuBlurred,
  menuItems,
  isSidebarFocused,
  isOpen,
  backToMainMenu,
  openSubMenu,
  path,
  shouldCloseIfEmpty,
  focusIdPrefix,
  disableSubMenuAutoOpen,
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [defaultFocusChild, onMove] = useSubMenuFocus(
    menuItems,
    isOpen,
    isSidebarFocused,
    focusIdPrefix + focusIds.SUB_MENU,
    focusIdPrefix,
    disableSubMenuAutoOpen,
  )

  const { checkIsActiveMenuItem, selectMenuItem } = useMenuItem(path)

  useEffect(() => {
    if (pathname.includes(path) && !disableSubMenuAutoOpen) {
      openSubMenu()
    }
  }, [pathname, disableSubMenuAutoOpen])

  useEffect(() => {
    if (!menuItems?.length && isOpen && shouldCloseIfEmpty) {
      backToMainMenu()
    }
  }, [menuItems, isOpen, shouldCloseIfEmpty])

  if (!isOpen || !menuItems?.length) {
    return null
  }

  const onLeft = () => backToMainMenu()

  return (
    <SubMainMenu
      elementType="nav"
      $subMenu={true}
      focusId={focusIdPrefix + focusIds.SUB_MENU}
      orientation={orientation.VERTICAL}
      defaultFocusChild={defaultFocusChild}
      onBlurred={onMenuBlurred}
      onFocused={onMenuFocused}
      onLeft={onLeft}
      onMove={onMove}
    >
      <ScrollContainer
        id={focusIdPrefix + scrollOptions.MenuScroll.containerId}
        height={scrollOptions.MenuScroll.height}
      >
        {menuItems.length ? (
          menuItems.map((menuItem: MenuItemType) => {
            return (
              <MenuItem
                key={menuItem.id}
                menuItem={menuItem}
                isSubMenuItem
                isSidebarFocused={isSidebarFocused}
                isActive={checkIsActiveMenuItem(menuItem)}
                onSelected={selectMenuItem}
                focusIdPrefix={focusIdPrefix}
              />
            )
          })
        ) : (
          <SubMenuPlaceholder $show={isSidebarFocused}>
            {t('emptySubMenu')}
          </SubMenuPlaceholder>
        )}
      </ScrollContainer>
    </SubMainMenu>
  )
}
